import type { AppNotification, ContentItem, FeedChannel, TourShow } from '../../domain/models'

export type Navigate = (to: string) => void

export const feedChannels: ReadonlyArray<{ id: FeedChannel; label: string }> = [
  { id: 'main', label: 'Main' },
  { id: 'updates', label: 'Band Wire' },
  { id: 'fits', label: 'Fits' },
  { id: 'merch', label: 'Merch' },
  { id: 'scene', label: 'Scene' },
]

export const contentTypeLabels: Record<ContentItem['type'], string> = {
  update: 'Update',
  'tour-note': 'Tour note',
  guide: 'Guide',
  destination: 'Destination',
}

export const notificationLabels: Record<AppNotification['kind'], string> = {
  show: 'Show',
  ticket: 'Ticket',
  community: 'Community',
  passport: 'Passport',
  system: 'Account',
}

export function citySlug(show: TourShow): string {
  return `${show.city}-${show.region}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
}

export function cityNameFromSlug(slug: string): string {
  return slug
    .split('-')
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ')
}

function parseVenueCalendarDate(value: string): Date | null {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return null
  const year = Number(match[1])
  const monthIndex = Number(match[2]) - 1
  const day = Number(match[3])
  const date = new Date(year, monthIndex, day, 12)
  if (
    Number.isNaN(date.getTime())
    || date.getFullYear() !== year
    || date.getMonth() !== monthIndex
    || date.getDate() !== day
  ) return null
  return date
}

export function formatShortDate(isoDate: string): string {
  const value = parseVenueCalendarDate(isoDate)
  if (!value) return isoDate
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  }).format(value)
}

export function formatLongDate(isoDate: string): string {
  const value = parseVenueCalendarDate(isoDate)
  if (!value) return isoDate
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'long',
    day: 'numeric',
  }).format(value)
}

export function formatDateTime(isoDate: string): string {
  const value = new Date(isoDate)
  if (Number.isNaN(value.getTime())) return isoDate
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(value)
}

export function showStatusLabel(show: TourShow): string {
  if (show.showStatus === 'check-in-open') return 'Check-in open'
  if (show.showStatus === 'complete') return 'Complete'
  return 'Upcoming'
}

export function showStatusTone(show: TourShow): 'active' | 'quiet' | 'complete' {
  if (show.showStatus === 'check-in-open') return 'active'
  if (show.showStatus === 'complete') return 'complete'
  return 'quiet'
}

export function sortedShows(shows: TourShow[]): TourShow[] {
  return [...shows].sort((left, right) => left.sequence - right.sequence)
}

export function isInternalPath(value: string | undefined): value is string {
  return Boolean(value?.startsWith('/'))
}
